import type { TermsDoc } from '@zuruflex/types';
import { Button, Input, cn } from '@zuruflex/ui';
import Link from '@tiptap/extension-link';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Heading2, Heading3, Italic, Link2, List, ListOrdered } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The editor a partner writes their terms in.
 *
 * StarterKit is cut down to exactly the node and mark set the server
 * allow-lists, rather than left at its defaults and trusted to behave.
 * The two lists have to agree: anything the editor can produce that
 * `termsDocSchema` does not know is a document the partner writes, saves,
 * and is refused — with no way to tell which keystroke did it.
 *
 * What is switched off is as deliberate as what is left on. Blockquotes,
 * code, rules and strikethrough are typography for an article; a rental
 * agreement is headings, paragraphs, lists and the occasional link, and
 * every extra control is one more thing that can make two partners' terms
 * look like different documents.
 */
export const TERMS_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    underline: false,
    // The starter kit's own link is replaced below, so the two do not both
    // register a mark of the same name.
    link: false,
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    // The server refuses anything but absolute http(s), so the editor
    // should not offer to make one it will not accept.
    protocols: ['http', 'https'],
  }),
];

export interface TermsEditorProps {
  doc: TermsDoc;
  /**
   * Which stored document this is — a `PartnerTerms` id, or null before one
   * has loaded.
   *
   * The editor owns its content once it is mounted, so `doc` cannot be
   * pushed back in on every render without fighting the caret. This says
   * when the thing being edited has *changed identity* — the page opened
   * and its query answered, or Zuruflex sent a version back — which is the
   * only moment the content should be replaced underneath the user.
   */
  documentKey: string | null;
  editable: boolean;
  onChange: (doc: TermsDoc) => void;
  /** Saved on blur rather than on every keystroke — see TermsPage. */
  onBlur?: () => void;
}

export function TermsEditor({
  doc,
  documentKey,
  editable,
  onChange,
  onBlur,
}: TermsEditorProps) {
  const { t } = useTranslation('partner');

  const editor = useEditor({
    extensions: TERMS_EXTENSIONS,
    content: doc,
    editable,
    editorProps: {
      attributes: {
        class: cn(
          'min-h-[24rem] max-w-none px-4 py-3 outline-none',
          'prose-sm text-sm leading-relaxed text-text-default',
          '[&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-bold',
          '[&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-bold',
          '[&_p]:mt-3 first:[&_p]:mt-0',
          '[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5',
          '[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_a]:text-accent [&_a]:underline',
        ),
        'aria-label': t('terms.editorLabel'),
      },
    },
    onUpdate: ({ editor: instance }) => onChange(instance.getJSON() as TermsDoc),
    onBlur: () => onBlur?.(),
  });

  /*
   * `useEditor` takes its content once, at mount — and at mount the page's
   * query has not answered, so that content is an empty document. Without
   * this, a partner who already has terms opens the page to a blank editor
   * and is one keystroke away from saving over them.
   *
   * Keyed on the row id rather than on `doc`, because `doc` changes on
   * every keystroke: re-setting the content then would reset the caret to
   * the top of the document after each character typed.
   */
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(doc, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, documentKey]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-border-default bg-surface">
      {editable ? <Toolbar editor={editor} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation(['partner', 'common']);

  /*
   * The link address is asked for in a row under the toolbar rather than in
   * `window.prompt`. A prompt is unstyled, blocks the whole page, is
   * suppressed outright in some embedded contexts, and cannot show the
   * address already on the selection — which is the thing somebody opening
   * it usually wants to edit.
   */
  const [linking, setLinking] = useState(false);
  const [href, setHref] = useState('');

  const openLink = () => {
    setHref((editor.getAttributes('link').href as string | undefined) ?? 'https://');
    setLinking((open) => !open);
  };

  const applyLink = () => {
    const value = href.trim();
    // An emptied box means "take the link off", which is why this is not
    // simply guarded on a non-empty value.
    if (value === '' || value === 'https://') {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: value }).run();
    }
    setLinking(false);
  };

  const tools = [
    {
      key: 'bold',
      icon: Bold,
      active: editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      key: 'italic',
      icon: Italic,
      active: editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: 'heading2',
      icon: Heading2,
      active: editor.isActive('heading', { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      key: 'heading3',
      icon: Heading3,
      active: editor.isActive('heading', { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      key: 'bulletList',
      icon: List,
      active: editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: 'orderedList',
      icon: ListOrdered,
      active: editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    { key: 'link', icon: Link2, active: editor.isActive('link') || linking, run: openLink },
  ] as const;

  return (
    <div className="border-b border-border-default bg-surface-sunken">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5">
        {tools.map((tool) => (
          <Button
            key={tool.key}
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-pressed={tool.active}
            aria-label={t(`partner:terms.tool.${tool.key}`)}
            title={t(`partner:terms.tool.${tool.key}`)}
            className={cn('size-8', tool.active && 'bg-accent/15 text-accent')}
            onClick={tool.run}
          >
            <tool.icon className="size-4" aria-hidden />
          </Button>
        ))}
      </div>

      {linking ? (
        <div className="flex items-center gap-2 border-t border-border-default px-2 py-2">
          <Input
            autoFocus
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
              if (event.key === 'Escape') setLinking(false);
            }}
            aria-label={t('partner:terms.linkPrompt')}
            placeholder="https://"
            className="h-8 flex-1"
          />
          <Button type="button" size="sm" onClick={applyLink}>
            {t('common:action.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
